import React from 'react';
import { CollaborativeAgreementUnsigned } from '@uplift/api';
import { useAgreementTemplatePreviewQuery, useCollaboratePhysicianMatchByIdForProviderQuery, useProviderDetailsQuery, useStateQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { Table, TableBody, TableRow, Typography } from '@mui/material';
import { useCurrentuser } from '@uplift/contexts';
import { AgreementText, DetailsCell, DocumentLayout, ReviewAndSignAction, agreementTermTypeLabelMap, useSignatureAnchor } from './components';
import { SignAgreementCard } from './SignAgreementCard';


export const CollaborativeAgreementUnsignedPreview: React.VFC<{
  document: CollaborativeAgreementUnsigned;
  onSign: (agreementId: string) => void;
}> = ({
  document,
  onSign,
}) => {
  const me = useCurrentuser();
  const { data: match } = useCollaboratePhysicianMatchByIdForProviderQuery(me.id, document.collaborativeMatchId);
  const { data: preview } = useAgreementTemplatePreviewQuery(document.agreementTemplateId, document.collaborativeMatchId);
  const { data: state } = useStateQuery(match.stateId, { suspense: true });
  const { data: physicianName } = useUserDisplayNameQuery(match.collaborativePhysicianId, { suspense: true });
  const { data: details } = useProviderDetailsQuery(match.providerId, { suspense: true });
  const { signatureRef, scrollToSignature } = useSignatureAnchor();

  return (
    <DocumentLayout
      title={preview.template.name}
      subtitle="Sent by BetterHelp"
      action={<ReviewAndSignAction onClick={scrollToSignature} />}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state.fullName}</DetailsCell>
            <DetailsCell label="Status">Pending your signature</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Effective Date">Upon counter-signature</DetailsCell>
            <DetailsCell label="Expires">{preview.template.agreementTermValue} {agreementTermTypeLabelMap[preview.template.agreementTermType][preview.template.agreementTermValue === 1 ? 'single' : 'many']} from effective date</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Collaborating Physician">{physicianName.fullLegalName}</DetailsCell>
            <DetailsCell label="NPI">{details.npi ?? '-'}</DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <AgreementText content={preview.version.content} />

      <SignAgreementCard ref={signatureRef} document={document} onSign={onSign} />
    </DocumentLayout>
  );
};
